/**
 * Client-side best-hand label (PokerStars-style "You have: Two Pair").
 * Visible only to the holder of hole cards — never trust for pot settlement.
 */

export type RankSuitCard = { rank: string; suit: string };

const RANK_VAL: Record<string, number> = {
  "2": 2,
  "3": 3,
  "4": 4,
  "5": 5,
  "6": 6,
  "7": 7,
  "8": 8,
  "9": 9,
  "10": 10,
  T: 10,
  J: 11,
  Q: 12,
  K: 13,
  A: 14,
};

function normSuit(s: string): string {
  const x = s.toLowerCase();
  if (x.startsWith("h")) return "h";
  if (x.startsWith("d")) return "d";
  if (x.startsWith("c")) return "c";
  if (x.startsWith("s")) return "s";
  return x[0] || "s";
}

function val(rank: string): number {
  const r = rank.toUpperCase();
  return RANK_VAL[r] ?? RANK_VAL[r.replace(/^0/, "")] ?? 0;
}

function combos<T>(arr: T[], k: number): T[][] {
  const res: T[][] = [];
  const walk = (start: number, path: T[]) => {
    if (path.length === k) {
      res.push(path.slice());
      return;
    }
    for (let i = start; i < arr.length; i++) {
      path.push(arr[i]!);
      walk(i + 1, path);
      path.pop();
    }
  };
  walk(0, []);
  return res;
}

function evalFive(
  cards: Array<{ v: number; s: string }>,
): { rank: number; name: string } {
  const values = cards.map((c) => c.v).sort((a, b) => b - a);
  const suits = cards.map((c) => c.s);
  const flush = suits.every((s) => s === suits[0]);
  const unique = [...new Set(values)];
  let straight = false;
  let highStraight = 0;
  if (unique.length === 5) {
    if (unique[0]! - unique[4]! === 4) {
      straight = true;
      highStraight = unique[0]!;
    }
    if (
      unique.includes(14) &&
      unique.includes(5) &&
      unique.includes(4) &&
      unique.includes(3) &&
      unique.includes(2)
    ) {
      straight = true;
      highStraight = 5;
    }
  }
  const counts = new Map<number, number>();
  for (const v of values) counts.set(v, (counts.get(v) ?? 0) + 1);
  const groups = [...counts.entries()].sort((a, b) => {
    if (b[1] !== a[1]) return b[1] - a[1];
    return b[0] - a[0];
  });
  const g0 = groups[0]?.[1] ?? 0;
  const g1 = groups[1]?.[1] ?? 0;

  if (straight && flush) {
    return {
      rank: highStraight === 14 ? 9 : 8,
      name: highStraight === 14 ? "Royal Flush" : "Straight Flush",
    };
  }
  if (g0 === 4) return { rank: 7, name: "Four of a Kind" };
  if (g0 === 3 && g1 === 2) return { rank: 6, name: "Full House" };
  if (flush) return { rank: 5, name: "Flush" };
  if (straight) return { rank: 4, name: "Straight" };
  if (g0 === 3) return { rank: 3, name: "Three of a Kind" };
  if (g0 === 2 && g1 === 2) return { rank: 2, name: "Two Pair" };
  if (g0 === 2) return { rank: 1, name: "One Pair" };
  const hi = values[0]!;
  const names: Record<number, string> = {
    14: "Ace High",
    13: "King High",
    12: "Queen High",
    11: "Jack High",
  };
  return { rank: 0, name: names[hi] || `${hi} High` };
}

/**
 * Best 5-card hold'em hand from hole + board.
 * Returns null if fewer than 2 hole cards or board+hole < 5 (show pair/high early).
 */
export function bestHandLabel(
  hole: RankSuitCard[] | undefined | null,
  board: RankSuitCard[] | undefined | null,
): string | null {
  if (!hole || hole.length < 2) return null;
  const all = [
    ...hole.map((c) => ({ v: val(c.rank), s: normSuit(c.suit) })),
    ...(board || []).map((c) => ({ v: val(c.rank), s: normSuit(c.suit) })),
  ].filter((c) => c.v > 0);

  if (all.length < 2) return null;

  // Preflop: pair or high card only
  if (all.length === 2) {
    if (all[0]!.v === all[1]!.v) return "One Pair";
    const hi = Math.max(all[0]!.v, all[1]!.v);
    const names: Record<number, string> = {
      14: "Ace High",
      13: "King High",
      12: "Queen High",
      11: "Jack High",
    };
    return names[hi] || "High Card";
  }

  if (all.length < 5) {
    // 3–4 cards: rough pair / trips check
    const counts = new Map<number, number>();
    for (const c of all) counts.set(c.v, (counts.get(c.v) ?? 0) + 1);
    const maxC = Math.max(...counts.values());
    if (maxC >= 3) return "Three of a Kind";
    if (maxC === 2) {
      const pairs = [...counts.values()].filter((n) => n === 2).length;
      return pairs >= 2 ? "Two Pair" : "One Pair";
    }
    return "High Card";
  }

  const fives = combos(all, 5);
  let best = { rank: -1, name: "High Card" };
  for (const five of fives) {
    const e = evalFive(five);
    if (e.rank > best.rank) best = e;
  }
  return best.name;
}
